import { TriangleAlert } from 'lucide-react';

import type { CandidateDetailData } from '@/modules/candidate-detail/types';
import {
  CandidateLedgerAssessment,
  CandidatePrescreenContext,
} from '@/modules/candidates/components/CandidateLedgerAssessment';
import type { CandidateStatusApi } from '@/modules/candidates/types';

import { CandidateFindingList } from './CandidateFindingList';
import { CandidatePrescreenCallNotes } from './CandidatePrescreenCallNotes';
import { CandidateSectionTitle } from './CandidateSectionTitle';
import { HiringManagerPrescreenNotes } from './HiringManagerPrescreenNotes';

interface CandidatePrescreenProps {
  candidate: CandidateDetailData;
  jobId: string;
  status: CandidateStatusApi | null;
}

export function CandidatePrescreen({
  candidate,
  jobId,
  status,
}: CandidatePrescreenProps) {
  if (candidate.screeningMethod !== 'HiringManager') {
    return <CandidatePrescreenAssessment candidate={candidate} />;
  }

  // The hiring manager's notes lead; the assessment built from them follows once it is ready.
  const hasAssessment =
    candidate.assessment?.mode === 'ledger'
      ? Boolean(candidate.assessment.scoredLedger)
      : Boolean(
          candidate.assessment?.prescreenSummary ||
          candidate.assessment?.confirmedFacts.length ||
          candidate.assessment?.concerns.length,
        );
  return (
    <div className="flex flex-col gap-8">
      <HiringManagerPrescreenNotes
        candidate={candidate}
        jobId={jobId}
        status={status}
      />
      {hasAssessment && <CandidatePrescreenAssessment candidate={candidate} />}
    </div>
  );
}

/** The assessment from the pre-screen, with the full note it condensed under it. */
function CandidatePrescreenAssessment({
  candidate,
}: {
  candidate: CandidateDetailData;
}) {
  const assessment = candidate.assessment;
  // A hiring manager's own note is stored as typed and already shown in full
  // above (HiringManagerPrescreenNotes), so the disclosure would repeat it.
  const prescreenNotes = candidate.screeningMethod !== 'HiringManager' && (
    <CandidatePrescreenCallNotes candidateJobId={candidate.candidateJobId} />
  );

  if (assessment?.mode === 'ledger') {
    return (
      <div className="flex flex-col gap-6">
        {assessment.scoredLedger ? (
          <>
            <CandidateLedgerAssessment
              assessment={assessment.scoredLedger}
              title="Post-prescreen assessment"
              description="Profile and call evidence evaluated against the role requirements when the call is usable."
              showMetrics={false}
            />
          </>
        ) : (
          <p className="text-muted-foreground border-y py-4 text-[13px]">
            No ledger assessment is available yet.
          </p>
        )}
        {prescreenNotes}
        {candidate.prescreen && (
          <CandidatePrescreenContext context={candidate.prescreen} />
        )}
      </div>
    );
  }

  const hasContent = Boolean(
    assessment?.prescreenSummary ||
    assessment?.confirmedFacts.length ||
    assessment?.concerns.length,
  );

  if (!assessment || !hasContent) {
    return (
      <div className="flex flex-col gap-8">
        <p className="text-muted-foreground border-y py-4 text-[13px]">
          No pre-screen summary or confirmed facts were captured.
        </p>
        {prescreenNotes}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <section
        aria-labelledby="prescreen-summary-heading"
        className="flex flex-col gap-3"
      >
        <CandidateSectionTitle id="prescreen-summary-heading">
          Pre-screen summary
        </CandidateSectionTitle>
        {assessment.prescreenSummary ? (
          <p className="max-w-[64ch] text-[15px] leading-6">
            {assessment.prescreenSummary}
          </p>
        ) : (
          <p className="text-muted-foreground text-[13px]">
            No pre-screen summary was captured.
          </p>
        )}
      </section>

      <section
        aria-labelledby="confirmed-facts-heading"
        className="flex flex-col gap-3"
      >
        <CandidateSectionTitle id="confirmed-facts-heading">
          Confirmed facts
        </CandidateSectionTitle>
        <CandidateFindingList
          emptyText="No additional confirmed facts were captured."
          findings={assessment.confirmedFacts}
        />
      </section>

      {assessment.concerns.length > 0 && (
        <section
          aria-labelledby="prescreen-concerns-heading"
          className="flex flex-col gap-3"
        >
          <CandidateSectionTitle id="prescreen-concerns-heading">
            Concerns
          </CandidateSectionTitle>
          <CandidateFindingList
            emptyText="No concerns were recorded."
            findings={assessment.concerns}
            icon={TriangleAlert}
            iconClassName="text-amber-700"
          />
        </section>
      )}

      {prescreenNotes}
    </div>
  );
}
